import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSSE } from '../context/SSEContext';
import './NotificationBell.css';

function formatNotificationDate(value) {
  if (!value) return '';
  let strVal = String(value).trim();
  if (!strVal.endsWith('Z') && !/[+-]\d{2}:?\d{2}$/.test(strVal)) {
    strVal += 'Z';
  }
  const parsed = new Date(strVal);
  if (Number.isNaN(parsed.getTime())) return value;
  return `${parsed.toLocaleDateString()} ${parsed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

function NotificationBell({ auth }) {
  const {
    notifications = [],
    unreadCount = 0,
    markAsRead,
    markAllAsRead,
    deleteNotification
  } = useSSE();

  const [isOpen, setIsOpen] = useState(false);
  const [dropdownTop, setDropdownTop] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Dynamically calculate mobile position
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const updatePosition = () => {
        if (window.innerWidth <= 768) {
          const rect = dropdownRef.current.getBoundingClientRect();
          setDropdownTop(Math.round(rect.bottom + 8));
        } else {
          setDropdownTop(null);
        }
      };
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition, true);
      };
    }
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleNotificationClick = async (notif) => {
    try {
      if (!notif.isRead && markAsRead) {
        await markAsRead(notif.id);
      }
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }

    setIsOpen(false);
    if (notif.link) {
      navigate(notif.link);
    }
  };

  const handleDeleteNotification = async (e, notifId) => {
    e.stopPropagation();
    try {
      if (deleteNotification) {
        await deleteNotification(notifId);
      }
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      if (markAllAsRead) {
        await markAllAsRead();
      }
    } catch (err) {
      console.error('Error marking all read:', err);
    }
  };

  return (
    <div className="notification-bell-container" ref={dropdownRef}>
      <button
        type="button"
        className="notification-bell-btn"
        onClick={handleToggle}
        title="Notifications"
        aria-label="Notifications"
      >
        <svg
          className="notification-bell-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
        </svg>
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div
          className="notification-dropdown"
          style={dropdownTop !== null ? { top: `${dropdownTop}px` } : undefined}
        >
          <div className="notification-dropdown-header">
            <div className="notification-dropdown-title">
              <span>Notifications</span>
              {unreadCount > 0 && (
                <span className="notification-unread-pill">{unreadCount} new</span>
              )}
            </div>
            {notifications.length > 0 && (
              <button
                type="button"
                className="notification-mark-all-btn"
                onClick={handleMarkAllRead}
              >
                Mark all as read
              </button>
            )}
          </div>

          <div className="notification-list">
            {notifications.length === 0 ? (
              <div className="notification-empty">No notifications yet</div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  className={`notification-item ${!notif.isRead ? 'unread' : ''}`}
                  onClick={() => handleNotificationClick(notif)}
                >
                  <div className="notification-item-content">
                    <div className="notification-item-title">{notif.title}</div>
                    <div className="notification-item-message">{notif.message}</div>
                    <div className="notification-item-time">
                      {formatNotificationDate(notif.createdAt)}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="notification-delete-btn"
                    title="Delete notification"
                    onClick={(e) => handleDeleteNotification(e, notif.id)}
                  >
                    &times;
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;